import { getServiceUrl } from '@airclou/utils';
import { HTTP_TIMEOUT_MS } from '../config';
import { apiErrorFrom } from './errors';

const API_BASE_URL = getServiceUrl('dashboard');

export interface Company {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  workspace_count?: number;
  member_count?: number;
  group_count?: number;
}

export interface CompanyUser {
  user_id: string;
  company_id: string;
  role: 'admin' | 'member';
  created_at: string;
  user?: {
    email: string;
    name: string;
  };
}

export interface CreateCompanyInput {
  name: string;
  slug: string;
  metadata?: Record<string, any>;
}

export interface UpdateCompanyInput {
  name?: string;
  slug?: string;
  metadata?: Record<string, any>;
}

export interface AddCompanyUserInput {
  user_id: string;
  role: 'admin' | 'member';
}

// Get auth token from localStorage
function getAuthToken(): string | null {
  return localStorage.getItem('dashboard:authToken');
}

// API functions
export async function listCompanies(): Promise<{ companies: Company[]; meta: { total: number } }> {
  const response = await fetch(`${API_BASE_URL}/api/companies`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch companies');
  }

  const json = await response.json();
  return json.data;
}

export async function getCompany(id: string): Promise<{ company: Company }> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${id}`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    // Carries the status so a caller can tell "this company is gone" from "the
    // request failed" — the detail page keeps itself on screen for one and swaps
    // to an error for the other.
    throw await apiErrorFrom(response, 'Failed to fetch company');
  }

  const json = await response.json();
  return json.data;
}

export async function createCompany(data: CreateCompanyInput): Promise<{ company: Company }> {
  const response = await fetch(`${API_BASE_URL}/api/companies`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to create company');
  }

  const json = await response.json();
  return json.data;
}

export async function updateCompany(id: string, data: UpdateCompanyInput): Promise<{ company: Company }> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${id}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to update company');
  }

  const json = await response.json();
  return json.data;
}

export async function deleteCompany(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to delete company');
  }
}

export async function listCompanyUsers(companyId: string): Promise<{ users: CompanyUser[] }> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/users`, {
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch company users');
  }

  const json = await response.json();
  return json.data;
}

export async function addCompanyUser(companyId: string, data: AddCompanyUserInput): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/users`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to add user to company');
  }
}

export async function updateCompanyUserRole(
  companyId: string,
  userId: string,
  role: 'admin' | 'member'
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json',
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to update user role');
  }
}

export async function removeCompanyUser(companyId: string, userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/companies/${companyId}/users/${userId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
    },
    signal: AbortSignal.timeout(HTTP_TIMEOUT_MS),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || error.error || 'Failed to remove user from company');
  }
}
